import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getDataWithoutParams } from 'src/consumer/services/featureServices'
import {
  createBookedEventLocalParticipantIdentity,
  FLOW_TYPES,
  decorateSessionWithRouting,
  readLocalParticipantIdentity,
  readSessionInterfaceId,
  resolveSessionRoutingContext,
} from 'src/live'

dayjs.extend(utc)
dayjs.extend(timezone)
import { getDataWithParams } from 'src/consumer/services/profileService'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { 
  isCommonResponseSuccessful, 
  isCommonResponseSuccessfulV2,
  markBookedSessionAsCompletedFunction 
} from 'src/mentor/helpers/utilityFunctions'
import { generateUidForAgora } from 'src/consumer/helpers/utilFunctions'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { sortEventsInOrder } from '../LiveSessionPageComponents'

const inFlightMentorBookedSlotsRequests = new Map()
const mentorBookedSlotsCache = new Map()
const MENTOR_BOOKED_SLOTS_CACHE_TTL_MS = 15000

export const sessionService = {
  // Get booked event details
  async getBookedEventDetails(bookedEventId, withSlug = false) {
    const url = withSlug 
      ? `/api/secure/user/booked-slot/details?slug=${bookedEventId}&send-auxiliary-data=true`
      : `/api/secure/user/booked-slot/details?eventId=${bookedEventId}&send-auxiliary-data=true`

    const response = await getDataWithoutParams(url, withSlug)
    
    // Check if response is an error object (network error, axios error, etc.)
    if (response?.isAxiosError || response?.message || (response?.code && !response?.data)) {
      // This is a network/axios error
      const errorMessage = response?.message || response?.response?.data?.message || 'Network error occurred'
      const errorCode = response?.code || response?.response?.status || 'NETWORK_ERROR'
      
      console.error('❌ Network error fetching booked event details:', {
        bookedEventId,
        withSlug,
        errorMessage,
        errorCode,
        isAxiosError: response?.isAxiosError,
        responseStatus: response?.response?.status,
      })
      
      return {
        success: false,
        errorData: {
          message: errorMessage,
          code: errorCode,
          isNetworkError: true,
          originalError: {
            code: response?.code,
            message: response?.message,
            responseStatus: response?.response?.status,
            responseData: response?.response?.data,
          },
        },
      }
    }
    
    // Check if response has valid data structure
    if (
      response?.status === 200 &&
      isCommonResponseSuccessful(response?.data?.code) &&
      response?.data?.data?.bookedEvent &&
      Object.keys(response?.data?.data?.bookedEvent)?.length !== 0
    ) {
      const { bookedEvent, ...auxiliaryDataFromApi } = response.data.data
      const backendSessionRoutingDecision =
        auxiliaryDataFromApi?.sessionRoutingDecision || bookedEvent?.sessionRoutingDecision || null
      const routingContext = resolveSessionRoutingContext({
        flowType: FLOW_TYPES.consumerLiveSession,
        sessionData: {
          ...bookedEvent,
          sessionRoutingDecision: backendSessionRoutingDecision,
        },
      })

      return {
        bookedEvent: decorateSessionWithRouting(bookedEvent, {
          flowType: FLOW_TYPES.consumerLiveSession,
          sessionRoutingDecision:
            backendSessionRoutingDecision || routingContext.sessionRoutingDecision,
        }),
        auxiliaryData: {
          ...auxiliaryDataFromApi,
          sessionInterfaceId: readSessionInterfaceId(
            auxiliaryDataFromApi,
            FLOW_TYPES.consumerLiveSession,
          ),
          localParticipantIdentity: readLocalParticipantIdentity(
            auxiliaryDataFromApi,
            createBookedEventLocalParticipantIdentity(bookedEvent, bookedEvent?.userRole),
          ),
          legacyPlatformId: routingContext.legacyPlatformId,
          sessionMode: routingContext.sessionMode,
          rtcProviderResolved: routingContext.rtcProviderResolved,
          signalingProviderResolved: routingContext.signalingProviderResolved,
          sessionRoutingDecision:
            backendSessionRoutingDecision || routingContext.sessionRoutingDecision,
        },
        success: true,
      }
    }

    // API returned an error response (not a network error)
    return {
      success: false,
      errorData: response?.data?.data || {
        message: 'Failed to fetch booked event details',
        code: response?.data?.code || 'UNKNOWN_ERROR',
      },
    }
  },

  // Get people information for participants
  async getPeopleInformation(bookedEventId) {
    return await getDataWithParams('/api/secure/live/participants/profile-summary', {
      eventSlug: bookedEventId,
    })
  },

  // Get mentor's booked slots for the day
  async getMentorBookedSlots(eventData) {
    const startTime = dayjs
      .utc(eventData.startTime)
      .local()
      .startOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss[Z]')
    const endTime = dayjs
      .utc(eventData.startTime)
      .local()
      .endOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss[Z]')
    const requestKey = `${eventData?.mentorId || ''}::${startTime}::${endTime}`
    const cached = mentorBookedSlotsCache.get(requestKey)
    if (cached && Date.now() - cached.timestamp < MENTOR_BOOKED_SLOTS_CACHE_TTL_MS) {
      return cached.result
    }
    if (inFlightMentorBookedSlotsRequests.has(requestKey)) {
      return inFlightMentorBookedSlotsRequests.get(requestKey)
    }

    const requestPromise = (async () => {
      const response = await getMPDataWithoutParams(
        `/api/secure/teacher/booked-slots/all-with-patient-info?startTime=${startTime}&endTime=${endTime}&get-patient-details=true`,
      )

      if (
        response?.status === 200 &&
        isCommonResponseSuccessfulV2(response)
      ) {
        let patients = response?.data?.data?.patientInfo
        let mentorSlots = response?.data?.data?.bookedEvents

        mentorSlots = mentorSlots?.map((bInfo) => {
          const patInfo = patients?.find((pInfo) => pInfo?.id === bInfo?.patientId)
          return { ...bInfo, patientInfo: patInfo || null }
        })

        if (mentorSlots) {
          const result = {
            success: true,
            mentorSlots: sortEventsInOrder(mentorSlots, 'startTime', true),
          }
          mentorBookedSlotsCache.set(requestKey, { result, timestamp: Date.now() })
          return result
        }
      }

      return { success: false }
    })()

    inFlightMentorBookedSlotsRequests.set(requestKey, requestPromise)
    try {
      return await requestPromise
    } finally {
      inFlightMentorBookedSlotsRequests.delete(requestKey)
    }
  },

  // Process participant summaries
  processParticipantSummaries(eventInfoData, peopleInformationResponse, profileData) {
    const result = {
      userRole: null,
      peerChatId: null,
      peerChatName: null,
      peerChatImage: null,
    }

    const thisMentorData = peopleInformationResponse?.data?.data?.mentorList?.find(
      (item) => item?.id === eventInfoData?.mentorId,
    )
    const thisUserData = peopleInformationResponse?.data?.data?.userList?.find(
      (item) => item?.userId === eventInfoData?.userId,
    )

    if (eventInfoData?.mentorId === profileData?.teacher?.id) {
      result.userRole = 'mentor'
      if (
        peopleInformationResponse?.status === 200 &&
        isCommonResponseSuccessful(peopleInformationResponse?.data?.code)
      ) {
        result.peerChatId = thisUserData?.userId
        result.peerChatName = thisUserData?.name || thisUserData?.firstName
        result.peerChatImage = thisUserData?.profilePicId 
          ? getImageLink(thisUserData?.profilePicId) 
          : undefined
      }
    } else if (eventInfoData?.userId === profileData?.userId) {
      result.userRole = 'user'
      if (
        peopleInformationResponse?.status === 200 &&
        isCommonResponseSuccessful(peopleInformationResponse?.data?.code)
      ) {
        result.peerChatId = thisMentorData?.userId
        result.peerChatName = thisMentorData?.firstName || thisMentorData?.name
        result.peerChatImage = thisMentorData?.profilePicId 
          ? getImageLink(thisMentorData?.profilePicId) 
          : undefined
      }
    } else if (eventInfoData?.panelIds?.includes(profileData?.teacher?.id)) {
      result.userRole = 'panelist'
    }

    return result
  },

  // Mark session as completed
  async markSessionAsCompleted(bookedEventFinalData) {
    return await markBookedSessionAsCompletedFunction(bookedEventFinalData)
  },

  // Generate UID object
  generateUids() {
    return {
      uid: generateUidForAgora(),
      uidScreen: generateUidForAgora(),
      uidRecording: generateUidForAgora(),
    }
  },
}
